'use client';

import type { Address } from '@/api/types/public';
import AddressForm from '@/components/Address';
import EmailInput from '@/components/Form/EmailInput';
import { toast } from '@/components/Toast';
import { updateConsentsAfterAuth } from '@/hooks/useComplianceConsent';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import useResponsive from '@/hooks/useResponsive';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { font } from '@/theme/zeelool.theme';
import appCommunicationManager, {
  EAppCommunicationFuncName,
} from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { COMPLIANCE_SCENE_CODES } from '@/utils/constants';
import { env } from '@/utils/env';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import termsPcStyles from '../terms-conditions/pc.module.scss';
import termsWapStyles from '../terms-conditions/wap.module.scss';

const initialAddress = {
  firstname: '',
  lastname: '',
  country_id: env.NEXT_PUBLIC_ADDRESS_COUNTRY_ID,
  country: env.NEXT_PUBLIC_ADDRESS_COUNTRY,
  street: '',
  apartment: '',
  city: '',
  province: '',
  province_id: '',
  postcode: '',
  tel_code: env.NEXT_PUBLIC_ADDRESS_TEL_CODE,
  tel_country: env.NEXT_PUBLIC_ADDRESS_TEL_COUNTRY,
  telephone: '',
  cpf: '',
  default: 2,
  region_code: '',
} as Address;

interface DisputeResolutionApplicationProps {
  termsHtml: string;
}

interface SubmissionData {
  address: Address;
  email: string;
}

export default function DisputeResolutionApplication({
  termsHtml,
}: DisputeResolutionApplicationProps) {
  const token = useStore((state) => state.token);
  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const { navigate, inAppH5 } = useAppNavigation();
  const { isPc } = useResponsive();
  const { cnName } = useRouteMetadata();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const emailForm = useForm<{ email: string }>({
    defaultValues: { email: '' },
    mode: 'all',
  });

  const submitTermsUnsubscribe = useCallback(
    async ({ address, email }: SubmissionData) => {
      if (isSubmittingRef.current) return;

      isSubmittingRef.current = true;
      setIsSubmitting(true);
      try {
        await updateConsentsAfterAuth({
          sceneCode: COMPLIANCE_SCENE_CODES.TERMS_UNSUBSCRIBE,
          page: cnName,
          status: 2,
          showResponseToast: true,
          metadata: {
            firstname: address.firstname,
            lastname: address.lastname,
            country_id: address.country_id,
            country: address.country,
            street: address.street,
            apartment: address.apartment,
            city: address.city,
            province: address.province,
            province_id: address.province_id,
            postcode: address.postcode,
            tel_code: address.tel_code,
            telephone: address.telephone,
            email,
          },
        });
      } catch (error) {
        logger.error('Failed to submit terms unsubscribe application:', error);
        toast.error('Submission failed. Please try again.');
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    [cnName],
  );

  useEffect(() => {
    if (!inAppH5) return;

    let active = true;
    let callbackName = '';

    try {
      callbackName = appCommunicationManager.registerCallback(
        EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
        async (appToken: unknown) => {
          if (!active) return;

          const nextToken =
            typeof appToken === 'string' && appToken !== 'ClearToken' ? appToken : '';

          if (nextToken) {
            await setToken(nextToken);
            await refreshAccountInfo();
          } else {
            await setToken('');
            setUserInfo({});
          }
        },
        {},
        'BackTokenInfo' as EAppCommunicationFuncName,
      );
    } catch (error) {
      logger.error('Failed to initialize dispute application App auth:', error);
    }

    return () => {
      active = false;
      if (callbackName) appCommunicationManager.removeCallback(callbackName);
    };
  }, [inAppH5, refreshAccountInfo, setToken, setUserInfo]);

  const handleBeforeSubmit = () => {
    if (token) return true;

    if (inAppH5) {
      navigate('/login');
    } else {
      setShowLoginRegisterDialog({
        show: true,
        eventType: 'dispute_resolution_application',
        situation: '争议处理条款申请',
      });
    }

    return false;
  };

  const handleSubmit = async (address?: Address) => {
    const isEmailValid = await emailForm.trigger('email');
    if (!isEmailValid) return;

    if (!address || isSubmittingRef.current) return;

    const submission = {
      address,
      email: emailForm.getValues('email'),
    };

    if (!token) {
      if (inAppH5) {
        navigate('/login');
      } else {
        setShowLoginRegisterDialog({
          show: true,
          eventType: 'dispute_resolution_application',
          situation: '争议处理条款申请',
        });
      }
      return;
    }

    await submitTermsUnsubscribe(submission);
  };

  return (
    <main
      className={`mx-auto flex w-full max-w-[1440px] flex-col items-center bg-primary-white px-0 pb-[64px] pt-[24px] lg:px-[50px] lg:pb-[126px] lg:pt-[70px] ${inAppH5 ? '!pb-[300px]' : ''}`}
    >
      <header className="mb-[24px] flex w-full flex-col items-center px-[16px] text-center lg:mb-[48px] lg:px-0">
        <h1 className="w-full font-bb text-[32px] text-left leading-normal text-primary-black lg:text-[44px] lg:text-center">
          TERMS AND CONDITIONS
        </h1>
      </header>

      <section
        className={`${isPc ? termsPcStyles.main : termsWapStyles.main} mb-[32px] lg:mb-[48px] lg:w-full lg:max-w-[1340px]`}
        dangerouslySetInnerHTML={{ __html: termsHtml }}
      />

      <section className="w-full max-w-[836px] [&_.AddressFormFooter]:!static [&_.AddressFormFooter]:!px-[16px]">
        <AddressForm
          data={initialAddress}
          onBeforeSubmit={handleBeforeSubmit}
          showDefault={false}
          showCancel={false}
          submitLabel="Submit"
          submitFontFamily={font.bs}
          submitFontSizeWap="14px"
          loading={isSubmitting}
          saveBtnSxPc={{
            width: '350px',
            height: '48px',
            flex: 'none',
          }}
          onSubmit={handleSubmit}
          onInvalidSubmit={() => {
            void emailForm.trigger('email');
          }}
          formEndContent={
            <div className="w-full">
              <EmailInput
                name="email"
                control={emailForm.control}
                required
                formItemClassName="mb-0"
              />
            </div>
          }
        />
      </section>
    </main>
  );
}
